import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import Badge from "react-bootstrap/Badge";
import Modal from "react-bootstrap/Modal";

import BackendN6API from "../../api/backend/n6";
import OverlayLoading from "../../components/forms/OverlayLoading";
import BreadcrumbOne from "../../components/forms/BreadcrumbOne";
import CategorySelect from "../../components/forms/CategorySelect";
import PublishedSelect from "../../components/forms/PublishedSelect";
import LaravelPagination from "../../components/forms/LaravelPagination";
import ModalComponentN6 from "../../components/Modal/ModalComponentN6";
import ModalEditN6 from "../../components/Modal/ModalEditN6";
import { columns as n6Columns } from "./n6.import";
import { exportToCsv } from "../../utils/exportCsv";
import { useNavigate } from "react-router-dom";

const context = createContext();

const BackendN6Page = () => {
    const fetchParams = useRef({});

    const [data, setData] = useState({});
    const [paging, setPaging] = useState({ take: 10 });
    const [filter, setFilter] = useState({});
    const [loading, setLoading] = useState(false);
    const [dataModal, setDataModal] = useState(null);
    const [dataEditModal, setDataEditModal] = useState(null);

    const fetchList = useCallback((payload) => {
        setLoading(() => true);
        BackendN6API.list(payload)
            .then((r) => setData(() => r.data))
            .finally(() => setLoading(() => false));
    }, []);

    const state = useMemo(() => {
        return {
            ...data,
            onChangePage: (value) => {
                setPaging((original) => ({ ...original, page: value }));
            },
            onChangeFilter: (values) => {
                setFilter((original) => ({ ...original, ...values }));
            },
            onDeleteRecord: (id) => {
                setLoading(() => true);
                BackendN6API.delete(id).finally(() => fetchList(fetchParams.current));
            },
            onClickView: (value) => {
                setDataModal(() => value);
            },
            onClickEdit: (value) => {
                setDataEditModal(() => value);
            },
            onSaveEdit: (form) => {
                setLoading(() => true);
                BackendN6API.update(form.id, form)
                    .finally(() => {
                        setDataEditModal(null);
                        fetchList(fetchParams.current);
                    });
            },
            onClickExport: () => {
                setLoading(() => true);
                BackendN6API.list({ ...fetchParams.current, take: 100000, page: 1 })
                    .then((r) => {
                        exportToCsv('export_n6.csv', r.data.data, n6Columns);
                    })
                    .catch((err) => {
                        console.error(err);
                        alert("เกิดข้อผิดพลาดในการดึงข้อมูลส่งออก");
                    })
                    .finally(() => {
                        setLoading(() => false);
                    });
            },
            onClickAction: (type, custom = {}) => {
                setLoading(() => true);
                BackendN6API.action(type, { ...custom, filter: fetchParams.current?.filter ?? {} }).finally(() => fetchList(fetchParams.current));
            },
        };
    }, [fetchList, data]);

    useEffect(() => {
        fetchParams.current = { ...paging, filter: filter };
        fetchList(fetchParams.current);
    }, [fetchList, paging, filter]);

    return (
        <context.Provider value={state}>
            <div className="position-relative">
                <BreadcrumbOne name="จัดการข้อมูล Primer Validation" />

                <div className="mb-4">
                    <Header />
                </div>

                <div>
                    <Content />
                </div>

                <div>
                    <Footer />
                </div>

                {loading && <OverlayLoading />}

                <Modal show={Boolean(dataModal)} onHide={() => setDataModal(null)} fullscreen centered>
                    <Modal.Header closeButton>
                        <code className="fs-5 text-truncate">{dataModal?.unique_id}</code>
                    </Modal.Header>
                    <Modal.Body>
                        <ModalComponentN6 data={dataModal} />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" onClick={() => setDataModal(null)}>
                            ปิด
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={Boolean(dataEditModal)} onHide={() => setDataEditModal(null)} size="xl" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>แก้ไขข้อมูล {dataEditModal?.unique_id}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <ModalEditN6 data={dataEditModal} onSave={state.onSaveEdit} onCancel={() => setDataEditModal(null)} />
                    </Modal.Body>
                </Modal>
            </div>
        </context.Provider>
    );
};

const Header = () => {
    const local = useContext(context);
    const navigate = useNavigate();

    const [search, setSearch] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [published, setPublished] = useState("");
    const [showChangeCategoryModal, setShowChangeCategoryModal] = useState(false);
    const [dataChangeCategoryModal, setDataChangeCategoryModal] = useState("0");

    const handleClickApply = () => {
        local.onChangeFilter({ search, category_id: categoryId, published });
    };

    const handleClickDeleteAll = () => {
        confirm(`ต้องการลบทั้งหมด ${local.total} รายการใช่หรือไม่`) && local.onClickAction?.("delete");
    };

    const handleClickPublishAll = () => {
        confirm(`ต้องการเผยแพร่ทั้งหมด ${local.total} รายการใช่หรือไม่`) && local.onClickAction?.("publish");
    };

    const handleClickUnpublishAll = () => {
        confirm(`ต้องการซ่อนทั้งหมด ${local.total} รายการใช่หรือไม่`) && local.onClickAction?.("unpublish");
    };

    const handleClickChangeCategory = () => {
        setShowChangeCategoryModal(() => true);
    };

    const handleChangeCategory = () => {
        setShowChangeCategoryModal(() => false);
        local.onClickAction?.("change_category", { category_id: dataChangeCategoryModal });
    };

    return (
        <>
            <Row style={{ alignItems: "end" }}>
                <Col sm={12} xl={6} className="mb-sm-3 mb-xl-0">
                    <Row>
                        <Col sm={6} lg={4}>
                            <Form.Group>
                                <Form.Label>Search</Form.Label>
                                <Form.Control type="text" value={search} onChange={(e) => setSearch(e.target.value.trim())} />
                            </Form.Group>
                        </Col>
                        <Col sm={6} lg={4}>
                            <Form.Group>
                                <Form.Label>หมวดหมู่</Form.Label>
                                <CategorySelect value={categoryId} onChange={(e) => setCategoryId(e.target.value)} />
                            </Form.Group>
                        </Col>

                        <Col sm={6} lg={4}>
                            <Form.Group>
                                <Form.Label>เปิดเผย</Form.Label>
                                <PublishedSelect value={published} onChange={(e) => setPublished(e.target.value)} />
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>
                <Col sm={12} xl={6}>
                    <div className="d-flex gap-2 justify-content-end">
                        <Dropdown>
                            <Dropdown.Toggle variant="light" id="dropdown-basic">
                                การดำเนินการ
                                <Badge pill bg="secondary" className="mx-1 font-monospace">
                                    {local.total ?? 0}
                                </Badge>
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <Dropdown.Item onClick={handleClickDeleteAll}>ลบทั้งหมด</Dropdown.Item>
                                <Dropdown.Item onClick={handleClickChangeCategory}>เปลี่ยนหมวดหมู่</Dropdown.Item>
                                <Dropdown.Item onClick={handleClickPublishAll}>ทำให้เป็นสาธารณะ</Dropdown.Item>
                                <Dropdown.Item onClick={handleClickUnpublishAll}>ซ่อนจากสาธารณะ</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                        <Button onClick={handleClickApply}>กรองข้อมูล</Button>
                        <Button variant="success" onClick={local.onClickExport}>
                            ส่งออกข้อมูล
                        </Button>
                        <Button variant="warning" onClick={() => navigate("/backend/n6/import")}>
                            นำเข้าข้อมูล
                        </Button>
                    </div>
                </Col>
            </Row>

            <Modal show={showChangeCategoryModal} onHide={() => setShowChangeCategoryModal(false)} centered>
                <Modal.Header>เปลี่ยนหมวดหมู่</Modal.Header>
                <Modal.Body>
                    <CategorySelect hideOptionAll value={dataChangeCategoryModal} onChange={(e) => setDataChangeCategoryModal(e.target.value)} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowChangeCategoryModal(false)}>
                        ยกเลิก
                    </Button>
                    <Button onClick={handleChangeCategory}>เปลี่ยน</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

const Content = () => {
    const local = useContext(context);
    const data = local?.data ?? [];

    const onClickDelete = (v) => {
        confirm(`ต้องการลบ #${v.id} ใช่หรือไม่`) && local.onDeleteRecord?.(v.id);
    };

    return (
        <Table responsive striped bordered hover className="mb-0">
            <thead>
                <tr>
                    <th>#</th>
                    <th className="font-monospace" style={{ width: "100%" }}>
                        primer_name
                    </th>
                    <th className="font-monospace" style={{ width: 200, minWidth: 200, maxWidth: 200 }}>
                        image
                    </th>
                    <th className="font-monospace" style={{ width: 200, minWidth: 200, maxWidth: 200 }}>
                        category
                    </th>
                    <th className="text-center" style={{ width: 75, minWidth: 75, maxWidth: 75 }}>
                        เปิดเผย
                    </th>
                    <th style={{ width: 200, minWidth: 200, maxWidth: 200 }}>นำเข้าเมื่อ</th>
                    <th className="text-center" style={{ width: 120, minWidth: 120, maxWidth: 120 }}>
                        การดำเนินการ
                    </th>
                </tr>
            </thead>
            <tbody>
                {data.map((v, i) => (
                    <tr key={i}>
                        <td className="font-monospace">{v.id}</td>
                        <td className="font-monospace">{v.primer_name}</td>
                        <td className="font-monospace">
                            <a href={v.image_url} target="_blank">
                                {v.image}
                            </a>
                        </td>
                        <td>{v.category?.name ?? "-"}</td>
                        <td className="font-monospace text-center">
                            {v.published_at ? <Badge bg="success">YES</Badge> : <Badge bg="secondary">NO</Badge>}
                        </td>
                        <td className="font-monospace">{v.created_at}</td>
                        <td className="text-center">
                            <div className="btn-group">
                                <button className="btn btn-sm btn-light" onClick={() => local.onClickView?.(v)}>
                                    <FontAwesomeIcon icon={["fas", "eye"]} />
                                </button>
                                <button className="btn btn-sm btn-light" onClick={() => local.onClickEdit?.(v)}>
                                    <FontAwesomeIcon icon={["fas", "pen"]} />
                                </button>
                                <button className="btn btn-sm btn-danger" onClick={() => onClickDelete(v)}>
                                    <FontAwesomeIcon icon={["fas", "trash"]} />
                                </button>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};

const Footer = () => {
    const local = useContext(context);

    return (
        <div className="d-flex align-items-center flex-wrap mt-3">
            <div>
                ทั้งหมด <span className="font-monospace bg-light p-1 rounded mx-2">{local.total ?? 0}</span> รายการ
            </div>
            <div className="flex-fill"></div>
            <LaravelPagination {...local} />
        </div>
    );
};

export default BackendN6Page;
